import { Modal, Box, IconButton, Button, Tooltip, Typography, Stack } from '@mui/material';
import { ArrowLeft, ArrowRight, Check, Close, FileCopy } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Node } from '../../classes/CircularLinkedList';
import Individual from '../../types/individual';
import { ValueOrFunction } from '../../types/types';

interface ZoomModalProps {
  open: boolean;
  onClose: () => void;
  carouselNode: Node<Individual> | null;
  setCarouselNode: ValueOrFunction<Node<Individual> | null>;
  toggleSelected: (id: string) => void;
  isSelected: (id: string) => boolean;
  updatePopulation: () => void;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '100vw',
  maxHeight: '100vh',
  border: '2px solid #fff',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  // disable accessibility blue highlight focus
  '&:focus': {
    outline: 'none',
  },
} as const;

const imageStyle = {
  maxWidth: '100%',
  maxHeight: '100%',
};

const overlayStyle = {
  position: 'absolute',
  bottom: 0,
  width: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  textAlign: 'center',
  padding: '10px 5px',
  display: 'none',
} as const;

const buttonStyle = {
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  display: 'none',
  fontSize: '0.5em', // these two help
  paddingRight: '7px', // to center the x
} as const;

const closeButtonStyle = {
  ...buttonStyle,
  top: '5px',
  right: '5px',
};

const nextButtonStyle = {
  ...buttonStyle,
  top: '50%',
  transform: 'translateY(-50%)', // make sure bang in centre
  right: '5px',
};

const prevButtonStyle = {
  ...buttonStyle,
  top: '50%',
  transform: 'translateY(-50%)',
  left: '5px',
};

const selectedIconStyle = {
  ...buttonStyle,
  top: '5px',
  left: '5px',
};


export default function ZoomModal({
  open,
  onClose,
  carouselNode,
  setCarouselNode,
  toggleSelected,
  isSelected,
  updatePopulation,
}: ZoomModalProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isShowingSelected, setIsShowingSelected] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { imageUrl, genome: description } = carouselNode?.data ?? {};

  // Copy button handler
  const handleCopy = (text: string | undefined) => {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
      });
    }
  };

  const closeHandler = () => {
    setIsHovered(false);
    setIsShowingSelected(false);
    onClose();
  };

  const { nextNode, prevNode } = useMemo(
    () => ({
      nextNode: carouselNode?.next?.findRight(({ imageUrl }) => !!imageUrl) ?? null,
      prevNode: carouselNode?.prev?.findLeft(({ imageUrl }) => !!imageUrl) ?? null,
    }), [carouselNode]);

  const handleNextImage = useCallback(() => setCarouselNode(nextNode), [nextNode, setCarouselNode]);
  const handlePrevImage = useCallback(() => setCarouselNode(prevNode), [prevNode, setCarouselNode]);

  const handleToggleSelected = useCallback(() => {
    if (carouselNode == null) return;
    // update state
    toggleSelected(carouselNode?.data.id);
    // then for visual feedback in case the modal is not hovered
    setIsShowingSelected(true);
  }, [toggleSelected, carouselNode, setIsShowingSelected]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code === 'ArrowRight') {
      handleNextImage();
    } else if (event.code === 'ArrowLeft') {
      handlePrevImage();
    } else if (event.code === 'Space') {
      event.preventDefault();
      handleToggleSelected();
    } else if (event.code === 'Enter') {
      event.preventDefault();
      setIsHovered(false);
      setIsShowingSelected(false);
      onClose();
      updatePopulation();
    } else if (event.code === 'KeyZ') {
      setIsHovered(false);
      setIsShowingSelected(false);
      onClose();
    }
  }, [handleNextImage, handlePrevImage, handleToggleSelected, setIsHovered, setIsShowingSelected, onClose, updatePopulation]);

  useEffect(() => {
    if (open)
      window.addEventListener('keydown', handleKeyDown);
    return () => {
      if (open)
        window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, open]);

  const selected = useMemo(() => {
    if (carouselNode == null) return false;
    return isSelected(carouselNode?.data.id);
  }, [carouselNode, isSelected]);

  return (
    <Modal
      open={open}
      onClose={closeHandler}
      slotProps={{
	backdrop: {
	  style: {
	    backgroundColor: 'rgba(0, 0, 0, 0.9)',
	  }
	}
      }}
    >
      <Box
        sx={style}
        onMouseMove={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img src={imageUrl} alt={description} style={imageStyle} />
        {description && (
          <Box sx={{ ...overlayStyle, display: isHovered ? 'flex' : 'none', alignItems: 'center' }}>
            <span>{description}</span>
            <Button
              size="small"
              style={{ marginLeft: '8px', color: 'white', minWidth: 'auto' }}
              onClick={() => handleCopy(description)}
            >
              <FileCopy fontSize="small" />
            </Button>
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '2px',
                marginRight: '2px',
                visibility: isCopied ? 'visible' : 'hidden'
              }}
            >
              <Check fontSize="small" />
            </Box>
          </Box>
        )}

        <Tooltip title="Shortcut: Left Arrow">
          <IconButton
            size="small"
            style={{ ...prevButtonStyle, display: isHovered ? 'block' : 'none' }}
            onClick={handlePrevImage}
            disabled={!prevNode}
          >
            <ArrowLeft />
          </IconButton>
        </Tooltip>

        <Tooltip title="Shortcut: Right Arrow">
          <IconButton
            size="small"
            style={{ ...nextButtonStyle, display: isHovered ? 'block' : 'none' }}
            onClick={handleNextImage}
            disabled={!nextNode}
          >
            <ArrowRight />
          </IconButton>
        </Tooltip>

        <Tooltip enterDelay={250} title={(
          <Stack spacing={1}>
            <Typography>
              Press <strong>ESC</strong> to close the large image.
            </Typography>
            <Typography>
              Press <strong>Enter</strong> to close the large image <em>and start making new images</em> if you have two or more selected.
            </Typography>
            <Typography>
              You can select and deselect with the spacebar while in this mode, and use the left- and right-arrow keys to change images.
            </Typography>
            <Typography>
              Pressing <strong>Enter</strong> from the gallery view will open the large images. No mouse needed!
            </Typography>
          </Stack>
        )}>
          <IconButton
            size="small"
            style={{ ...closeButtonStyle, display: isHovered ? 'block' : 'none' }}
            onClick={closeHandler}
          >
            <Close />
          </IconButton>
        </Tooltip>

        <Tooltip title="Shortcut: Space">
          <Button
            size="small"
            style={{ ...selectedIconStyle, display: isShowingSelected || isHovered ? 'block' : 'none' }}
            onClick={handleToggleSelected}
          >
            <Typography variant="body2" display="flex" alignItems="center">
              {selected ? (
                <>
                  <Check fontSize="small" sx={{ marginRight: 0.5 }} />
                  Selected
                </>
              ) : (
                <>
                  <Close fontSize="small" sx={{ marginRight: 0.5 }} />
                  Unselected
                </>
              )}
            </Typography>
          </Button>
        </Tooltip>
      </Box >
    </Modal >
  );
}
